/**
 * Строка справочника с картинкой — и просмотр движения перед выбором.
 *
 * Зачем. Человек выбирает упражнение по названию и не всегда знает, что за
 * ним стоит: «Тяга горизонтального блока» или «Разведение в тренажёре» —
 * слова, которые в зале ни о чём не говорят, пока не увидишь снаряд.
 *
 * ПОСМОТРЕТЬ И ВЫБРАТЬ — РАЗНЫЕ КНОПКИ, и это главное решение файла. Тап по
 * названию выбирает движение, как и раньше, а в «Заменить» и на экране
 * тренировки выбор сразу меняет план на сервере (у процентной программы —
 * необратимо). Поэтому картинка — отдельная кнопка слева, со своей зоной
 * касания и зазором до названия: «хотел посмотреть — случайно выбрал» не
 * должно случаться. Выбрать можно и из просмотра, кнопкой над кадрами.
 *
 * ВЕС. В списке до сорока строк, а сеть в зале плохая. Миниатюра — отдельный
 * файл в 120 px (эскиз самого Викисклада, ~6 КБ), и грузится она только когда
 * строка видна или вот-вот станет видна. Полагаться на `loading="lazy"`
 * нельзя: внутри прокручиваемого блока браузер решает сам и тянет больше.
 *
 * НЕТ РИСУНКА. У части каталожных движений рисунка в свободных источниках нет,
 * у личных — не бывает. Каталожное движение всё равно получает кнопку
 * просмотра со знаком «?»: текст техники есть у всех. Личное — не получает:
 * технику за человека приложение не сочиняет.
 */

import { useEffect, useRef, useState, type ReactNode } from 'react'

import type { Exercise } from '../api/types'
import { TECHNIQUE_IMAGES } from '../technique'
import {
  Frames,
  TechniqueCredit,
  TechniqueText,
  hasImages,
  hasTechnique,
} from './TechniqueCard'

/** Адрес миниатюры. Выводится из id: хранить его вторым полем незачем. */
export function thumbUrl(exerciseId: number): string | null {
  return hasImages(exerciseId) ? `/technique/thumb/${exerciseId}.png` : null
}

/** Видна ли строка (с запасом в пару строк) — чтобы грузить только нужное. */
function useNearViewport<T extends HTMLElement>(): [React.RefObject<T>, boolean] {
  const ref = useRef<T>(null)
  // Без IntersectionObserver (очень старый браузер) грузим сразу: пустые
  // рамки вместо картинок хуже лишних килобайт.
  const [near, setNear] = useState(typeof IntersectionObserver === 'undefined')

  useEffect(() => {
    const node = ref.current
    if (near || !node) return
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          setNear(true)
          observer.disconnect()
        }
      },
      // Корень — ближайший прокручиваемый список: страница сама может и не
      // прокручиваться, а строки уезжают внутри блока.
      { root: node.closest('.se-list'), rootMargin: '160px 0px' },
    )
    observer.observe(node)
    return () => observer.disconnect()
  }, [near])

  return [ref, near]
}

function Thumb({ exercise, onPeek }: { exercise: Exercise; onPeek: () => void }) {
  const [ref, near] = useNearViewport<HTMLButtonElement>()
  const [broken, setBroken] = useState(false)

  if (exercise.is_custom || !hasTechnique(exercise.id)) return null

  const url = thumbUrl(exercise.id)
  // Рисунок изображает вариант движения (другой хват, другая постановка).
  // Правило проекта: вариант показывается только с честной пометкой — и
  // пометка обязана быть видна уже в списке, а не только в просмотре.
  const variant = Boolean(TECHNIQUE_IMAGES[exercise.id]?.note)

  return (
    <button
      ref={ref}
      type="button"
      className="xp-thumb"
      onClick={onPeek}
      aria-label={`Как выглядит: ${exercise.name}`}
    >
      {url && !broken ? (
        near && (
          <img
            src={url}
            alt=""
            width={48}
            height={48}
            decoding="async"
            onError={() => setBroken(true)}
          />
        )
      ) : (
        <span className="xp-glyph" aria-hidden="true">
          ?
        </span>
      )}
      {variant && url && !broken && <span className="xp-variant">вариант</span>}
    </button>
  )
}

export function ExerciseRow({
  exercise,
  reason,
  pill,
  disabled,
  onPick,
  onPeek,
  extra,
}: {
  exercise: Exercise
  /** Причина второй строкой: справа пилюлей она сжимала бы название в столбик. */
  reason?: string
  pill?: string
  disabled: boolean
  onPick: () => void
  onPeek: () => void
  /** Дополнительная кнопка справа (убрать своё движение). */
  extra?: ReactNode
}) {
  return (
    <div className="se-row xp-row">
      <Thumb exercise={exercise} onPeek={onPeek} />
      <button type="button" className="se-pick xp-pick" disabled={disabled} onClick={onPick}>
        <span className="xp-text">
          <span>{exercise.name}</span>
          {reason && <span className="xp-reason">{reason}</span>}
        </span>
        {pill && <span className="pill">{pill}</span>}
      </button>
      {extra}
    </div>
  )
}

/**
 * Просмотр движения: кадры, пометка варианта, текст техники, источники.
 *
 * Открывается на месте списка, а не внутри него: высокий кадр в блоке высотой
 * 300 px утащил бы кнопку «Выбрать» за край. Кнопки стоят НАД кадрами — чтобы
 * решение не требовало прокрутки.
 */
export function ExercisePeek({
  exercise,
  pickLabel,
  disabled,
  onPick,
  onBack,
}: {
  exercise: Exercise
  pickLabel: string
  disabled: boolean
  onPick: () => void
  onBack: () => void
}) {
  const ref = useRef<HTMLDivElement>(null)
  // Просмотр встаёт на место списка, а страницу могли прокрутить далеко вниз:
  // кнопки «К списку» и выбора оказались бы над краем экрана.
  useEffect(() => {
    const node = ref.current
    if (node && node.getBoundingClientRect().top < 0) node.scrollIntoView({ block: 'start' })
  }, [exercise.id])

  return (
    <div className="xp-peek" ref={ref}>
      <div className="se-actions" style={{ marginTop: 0 }}>
        <button type="button" className="btn ghost" onClick={onBack}>
          ← К списку
        </button>
        <button type="button" className="btn primary" disabled={disabled} onClick={onPick}>
          {pickLabel}
        </button>
      </div>

      <div className="ex-name" style={{ marginTop: 12 }}>
        {exercise.name}
      </div>

      {hasImages(exercise.id) ? (
        <Frames exerciseId={exercise.id} name={exercise.name} />
      ) : (
        <div className="faint" style={{ marginTop: 8 }}>
          Рисунка этого движения в свободных источниках нет — ниже только текст.
        </div>
      )}

      <TechniqueText exerciseId={exercise.id} />
      <TechniqueCredit exerciseIds={[exercise.id]} />
    </div>
  )
}
